// Greedy Meshing eines Chunks: reine Funktion von Welt zu Typed Arrays, ohne Three.js, damit sie
// in Vitest läuft. Für jede der sechs Richtungen wird Ebene für Ebene eine Maske der sichtbaren
// Flächen gebaut und zu möglichst großen Rechtecken gleicher Block-id zusammengelegt. Zaun und
// Pfosten sind keine vollen Würfel und werden je Block als schmale Quader angehängt.
//
// Sichtbarkeit (ADR 0017, ersetzt ADR 0011 Punkt 2): Gezeichnet wird jeder Block mit
// `shape: 'cube'` außer Luft, also auch Wasser und Tür mit deckender Textur. Verdeckt wird eine
// Fläche aber nur von einem festen vollen Würfel (`solid && shape === 'cube'`) oder von demselben
// Block (Wasser an Wasser, Tür über Tür). Tür und Wasser sind nicht fest, man steht in ihnen; die
// Flächen von Boden, Sturz und Wänden zum Türblock hin müssen darum vorhanden sein, sonst sieht
// man von innen den Himmel. Nachbarn werden über `getBlock` der Welt gelesen, damit an
// Chunk-Grenzen keine Innenflächen bleiben; außerhalb der Welt ist Luft, der Weltrand bekommt
// also Flächen.
//
// Texturkoordinaten (ADR 0011): UVs liegen in Blockeinheiten (0..Breite, 0..Höhe), dazu kommt je
// Vertex das Kachel-Rechteck [u0, v0, u1, v1] des Atlas als eigenes Attribut. Der Shader wickelt
// `fract(uv)` in das Rechteck; so wiederholt sich eine Kachel über ein ganzes Greedy-Rechteck.

import {
  CHUNK_SIZE,
  WORLD_HEIGHT,
  blockDef,
  blockIndex,
  getBlock,
  getChunk,
  type BlockId,
  type World,
} from '@bauernhof/shared';

/** Welche Kachel eine Blockseite bekommt: Oberseite, Seiten oder Unterseite. */
export type BlockFace = 'top' | 'side' | 'bottom';

/** Kachel-Rechteck im Atlas als [u0, v0, u1, v1] in Texturkoordinaten 0..1; v0 ist unten. */
export type TileRect = readonly [number, number, number, number];

/** Was der Mesher vom Atlas wissen muss; `atlas.ts` liefert die Umsetzung. */
export interface AtlasLayout {
  tile(id: BlockId, face: BlockFace): TileRect;
}

/** Geometrie eines Chunks in Weltkoordinaten: je Vertex Position, Normale, UV und Kachel. */
export interface ChunkMesh {
  positions: Float32Array;
  normals: Float32Array;
  uvs: Float32Array;
  /** Vier Werte je Vertex: das Kachel-Rechteck [u0, v0, u1, v1]. */
  tiles: Float32Array;
  indices: Uint32Array;
}

type Achse = 0 | 1 | 2;
type Vec = [number, number, number];

/**
 * Eine der sechs Blickrichtungen. `uAxis`/`vAxis` sind so gewählt, dass u × v = normal gilt;
 * dann liegen die Ecken (u0,v0), (u1,v0), (u1,v1), (u0,v1) von außen gesehen gegen den
 * Uhrzeigersinn. `s`/`t` sind die Texturachsen: s wächst nach rechts, t nach oben, jeweils von
 * außen betrachtet; auf Ober- und Unterseite zeigt t nach Norden (−z).
 */
interface Richtung {
  normal: Vec;
  axis: Achse;
  sign: 1 | -1;
  uAxis: Achse;
  vAxis: Achse;
  sAxis: Achse;
  sSign: 1 | -1;
  tAxis: Achse;
  tSign: 1 | -1;
  face: BlockFace;
}

const RICHTUNGEN: readonly Richtung[] = [
  { normal: [1, 0, 0], axis: 0, sign: 1, uAxis: 1, vAxis: 2, sAxis: 2, sSign: -1, tAxis: 1, tSign: 1, face: 'side' },
  { normal: [-1, 0, 0], axis: 0, sign: -1, uAxis: 2, vAxis: 1, sAxis: 2, sSign: 1, tAxis: 1, tSign: 1, face: 'side' },
  { normal: [0, 1, 0], axis: 1, sign: 1, uAxis: 2, vAxis: 0, sAxis: 0, sSign: 1, tAxis: 2, tSign: -1, face: 'top' },
  { normal: [0, -1, 0], axis: 1, sign: -1, uAxis: 0, vAxis: 2, sAxis: 0, sSign: -1, tAxis: 2, tSign: -1, face: 'bottom' },
  { normal: [0, 0, 1], axis: 2, sign: 1, uAxis: 0, vAxis: 1, sAxis: 0, sSign: 1, tAxis: 1, tSign: 1, face: 'side' },
  { normal: [0, 0, -1], axis: 2, sign: -1, uAxis: 1, vAxis: 0, sAxis: 0, sSign: -1, tAxis: 1, tSign: 1, face: 'side' },
];

/** Ausdehnung eines Chunks je Achse. */
const DIMS: Vec = [CHUNK_SIZE, WORLD_HEIGHT, CHUNK_SIZE];

/** Halbe Breite von Zaun und Pfosten: 0,25 breit, mittig im Block. */
const SCHMAL = 0.125;
/** Die zwei Querlatten eines Zaunarms: [Unterkante, Oberkante] relativ zum Block, je 3/16 hoch. */
const LATTEN: readonly [number, number][] = [
  [0.375, 0.5625],
  [0.75, 0.9375],
];

/** Wird als voller Würfel gezeichnet: jeder Würfelblock außer Luft, also auch Wasser und Tür. */
function wirdAlsWuerfelGezeichnet(id: BlockId): boolean {
  return id !== 0 && blockDef(id).shape === 'cube';
}

/** Ein fester voller Würfel; nur er verdeckt fremde Nachbarflächen (nicht Wasser, nicht Tür). */
function istFesterWuerfel(id: BlockId): boolean {
  const def = blockDef(id);
  return def.solid && def.shape === 'cube';
}

/**
 * Ob `nachbar` die ihm zugewandte Fläche des Blocks `id` verdeckt: ein fester voller Würfel immer,
 * derselbe Block ebenfalls, damit Wasser an Wasser und Tür über Tür keine Innenflächen bekommen.
 */
function verdeckt(nachbar: BlockId, id: BlockId): boolean {
  return nachbar === id || istFesterWuerfel(nachbar);
}

/**
 * Woran sich ein Zaunsegment anschließt: ein fester Zaun oder Pfosten oder ein fester voller
 * Würfel. Das offene Tor (`tor_offen`, nicht fest) ist eine Lücke und bekommt keinen Arm.
 */
function verbindetZaun(id: BlockId): boolean {
  const def = blockDef(id);
  return def.solid && (def.shape === 'fence' || def.shape === 'post' || def.shape === 'cube');
}

/**
 * Wird als schmaler Quader gezeichnet: fester Zaun und Pfosten. Das offene Tor hat dieselbe Form,
 * ist aber nicht fest – es steht offen und wird darum gar nicht gezeichnet, damit man den
 * Durchgang sieht (ADR 0022).
 */
function wirdAlsSchmalGezeichnet(id: BlockId): boolean {
  const def = blockDef(id);
  return def.solid && (def.shape === 'fence' || def.shape === 'post');
}

/** Sammelt Vertices und Indizes und liefert am Ende die Typed Arrays. */
class MeshBuilder {
  private readonly positions: number[] = [];
  private readonly normals: number[] = [];
  private readonly uvs: number[] = [];
  private readonly tiles: number[] = [];
  private readonly indices: number[] = [];

  /**
   * Ein Rechteck mit vier Ecken in Weltkoordinaten, in der Reihenfolge gegen den Uhrzeigersinn
   * von außen. UVs entstehen aus den Weltkoordinaten in Blockeinheiten, verschoben auf die
   * Blockkante, damit ein Teilquader (Zaun) den passenden Ausschnitt seiner Kachel zeigt.
   */
  quad(richtung: Richtung, ecken: readonly Vec[], tile: TileRect): void {
    const s = ecken.map((e) => richtung.sSign * e[richtung.sAxis]);
    const t = ecken.map((e) => richtung.tSign * e[richtung.tAxis]);
    const s0 = Math.floor(Math.min(...s));
    const t0 = Math.floor(Math.min(...t));
    const start = this.positions.length / 3;
    ecken.forEach((ecke, i) => {
      this.positions.push(ecke[0], ecke[1], ecke[2]);
      this.normals.push(richtung.normal[0], richtung.normal[1], richtung.normal[2]);
      this.uvs.push((s[i] ?? 0) - s0, (t[i] ?? 0) - t0);
      this.tiles.push(tile[0], tile[1], tile[2], tile[3]);
    });
    this.indices.push(start, start + 1, start + 2, start, start + 2, start + 3);
  }

  /** Ein achsenparalleler Quader von `min` bis `max`; `weglassen` nennt Richtungen ohne Fläche. */
  box(min: Vec, max: Vec, id: BlockId, layout: AtlasLayout, weglassen: readonly Richtung[]): void {
    for (const r of RICHTUNGEN) {
      if (weglassen.includes(r)) continue;
      const ebene = r.sign > 0 ? max[r.axis] : min[r.axis];
      const ecken = rechteck(r, ebene, min[r.uAxis], max[r.uAxis], min[r.vAxis], max[r.vAxis]);
      this.quad(r, ecken, layout.tile(id, r.face));
    }
  }

  build(): ChunkMesh {
    return {
      positions: Float32Array.from(this.positions),
      normals: Float32Array.from(this.normals),
      uvs: Float32Array.from(this.uvs),
      tiles: Float32Array.from(this.tiles),
      indices: Uint32Array.from(this.indices),
    };
  }
}

/** Die vier Ecken eines Rechtecks in der Ebene `ebene` der Richtung, gegen den Uhrzeigersinn. */
function rechteck(r: Richtung, ebene: number, u0: number, u1: number, v0: number, v1: number): Vec[] {
  const ecke = (u: number, v: number): Vec => {
    const p: Vec = [0, 0, 0];
    p[r.axis] = ebene;
    p[r.uAxis] = u;
    p[r.vAxis] = v;
    return p;
  };
  return [ecke(u0, v0), ecke(u1, v0), ecke(u1, v1), ecke(u0, v1)];
}

/**
 * Erzeugt die Geometrie des Chunks (cx, cz) in Weltkoordinaten. Reine Funktion; ein unbekannter
 * Chunk ist ein Programmierfehler und wirft.
 */
export function meshChunk(world: World, cx: number, cz: number, layout: AtlasLayout): ChunkMesh {
  const chunk = getChunk(world, cx, cz);
  if (chunk === undefined) {
    throw new Error(`Chunk (${cx}, ${cz}) liegt außerhalb der Welt (${world.chunksX} × ${world.chunksZ}).`);
  }
  const ursprung: Vec = [cx * CHUNK_SIZE, 0, cz * CHUNK_SIZE];
  const blocks = chunk.blocks;
  const builder = new MeshBuilder();

  for (const r of RICHTUNGEN) {
    const dimU = DIMS[r.uAxis];
    const dimV = DIMS[r.vAxis];
    const dimA = DIMS[r.axis];
    const maske = new Uint8Array(dimU * dimV);
    const p: Vec = [0, 0, 0];

    for (let ebene = 0; ebene < dimA; ebene++) {
      // Maske: Block-id, wo eine Fläche in diese Richtung sichtbar ist, sonst 0.
      let sichtbar = false;
      for (let v = 0; v < dimV; v++) {
        for (let u = 0; u < dimU; u++) {
          p[r.axis] = ebene;
          p[r.uAxis] = u;
          p[r.vAxis] = v;
          const id = blocks[blockIndex(p[0], p[1], p[2])] ?? 0;
          let wert = 0;
          if (wirdAlsWuerfelGezeichnet(id)) {
            const nachbar = getBlock(
              world,
              ursprung[0] + p[0] + r.normal[0],
              ursprung[1] + p[1] + r.normal[1],
              ursprung[2] + p[2] + r.normal[2],
            );
            if (!verdeckt(nachbar, id)) wert = id;
          }
          maske[v * dimU + u] = wert;
          if (wert !== 0) sichtbar = true;
        }
      }
      if (!sichtbar) continue;

      // Greedy: von jeder noch offenen Zelle aus erst in u, dann in v so weit wie möglich wachsen.
      const flaeche = ebene + (r.sign > 0 ? 1 : 0) + ursprung[r.axis];
      for (let v = 0; v < dimV; v++) {
        for (let u = 0; u < dimU; u++) {
          const id = maske[v * dimU + u] ?? 0;
          if (id === 0) continue;
          let breite = 1;
          while (u + breite < dimU && maske[v * dimU + u + breite] === id) breite++;
          let hoehe = 1;
          while (v + hoehe < dimV && zeilePasst(maske, (v + hoehe) * dimU + u, breite, id)) hoehe++;
          for (let dv = 0; dv < hoehe; dv++) maske.fill(0, (v + dv) * dimU + u, (v + dv) * dimU + u + breite);

          const u0 = u + ursprung[r.uAxis];
          const v0 = v + ursprung[r.vAxis];
          builder.quad(r, rechteck(r, flaeche, u0, u0 + breite, v0, v0 + hoehe), layout.tile(id, r.face));
        }
      }
    }
  }

  // Zaun und Pfosten je Block, ohne Greedy.
  for (let y = 0; y < WORLD_HEIGHT; y++) {
    for (let z = 0; z < CHUNK_SIZE; z++) {
      for (let x = 0; x < CHUNK_SIZE; x++) {
        const id = blocks[blockIndex(x, y, z)] ?? 0;
        if (!wirdAlsSchmalGezeichnet(id)) continue;
        // `wirdAlsSchmalGezeichnet` hat die Form schon geprüft; hier bleibt nur fence oder post.
        const form = blockDef(id).shape === 'post' ? 'post' : 'fence';
        schmalerBlock(builder, world, layout, id, form, ursprung[0] + x, y, ursprung[2] + z);
      }
    }
  }

  return builder.build();
}

function zeilePasst(maske: Uint8Array, start: number, breite: number, id: number): boolean {
  for (let k = 0; k < breite; k++) {
    if (maske[start + k] !== id) return false;
  }
  return true;
}

/**
 * Zaun oder Pfosten an Weltposition (x, y, z): ein Pfosten 0,25 × 0,25 × 1 in der Blockmitte,
 * beim Zaun zusätzlich je angeschlossenem Nachbarn ein Arm aus zwei Querlatten bis zur
 * Blockkante. Flächen im Inneren der Verbindung (Latte gegen Pfosten, Latte gegen
 * Nachbarsegment) entfallen; ebenso Ober- und Unterseite des Pfostens gegen einen festen vollen
 * Würfel (Wasser und Tür verdecken auch hier nicht).
 */
function schmalerBlock(
  builder: MeshBuilder,
  world: World,
  layout: AtlasLayout,
  id: BlockId,
  form: 'fence' | 'post',
  x: number,
  y: number,
  z: number,
): void {
  const mitteX = x + 0.5;
  const mitteZ = z + 0.5;
  const weglassenPfosten = RICHTUNGEN.filter(
    (r) => r.axis === 1 && istFesterWuerfel(getBlock(world, x, y + r.sign, z)),
  );
  const arme = form === 'fence' ? RICHTUNGEN.filter((r) => r.axis !== 1 && verbindetZaun(getBlock(world, x + r.normal[0], y, z + r.normal[2]))) : [];

  builder.box([mitteX - SCHMAL, y, mitteZ - SCHMAL], [mitteX + SCHMAL, y + 1, mitteZ + SCHMAL], id, layout, weglassenPfosten);

  for (const r of arme) {
    // Die Latten reichen auf ihrer Achse vom Pfostenrand bis zur Blockkante, quer bleiben sie
    // schmal; zum Pfosten hin (Gegenrichtung) und zum Nachbarn hin haben sie keine Fläche.
    const mitte = r.axis === 0 ? mitteX : mitteZ;
    const kante = r.axis === 0 ? x : z;
    const gegen = RICHTUNGEN.filter((g) => g.axis === r.axis);
    for (const [unten, oben] of LATTEN) {
      const min: Vec = [mitteX - SCHMAL, y + unten, mitteZ - SCHMAL];
      const max: Vec = [mitteX + SCHMAL, y + oben, mitteZ + SCHMAL];
      if (r.sign > 0) {
        min[r.axis] = mitte + SCHMAL;
        max[r.axis] = kante + 1;
      } else {
        min[r.axis] = kante;
        max[r.axis] = mitte - SCHMAL;
      }
      builder.box(min, max, id, layout, gegen);
    }
  }
}
